import { iconForPath } from "./bo-pages";
import {
  type Destination,
  type DestinationContext,
  resolveDestination,
  route,
} from "./destinations";
import { RECORD_SHORTCUT_ICONS, recordShortcutPath } from "./record-shortcuts";
import { findCatalogShortcut } from "./shortcut-catalog";
import {
  isCustomShortcut,
  isRecordShortcut,
  type ShortcutIcon,
  type StoredShortcut,
} from "./shortcuts";

export type ResolvedShortcut = {
  id: string;
  label: string;
  icon: ShortcutIcon;
  destination: Exclude<Destination, null>;
};

type VisibleT = (key: string, fallback: string) => string;

/**
 * Raccourcis affichables pour cette instance, dans l'ordre de l'utilisateur.
 * Ceux dont la destination n'existe pas ici (e-shop absent…) sont omis mais
 * restent enregistrés.
 */
export function resolveShortcuts(
  stored: StoredShortcut[],
  ctx: DestinationContext,
  vt: VisibleT,
): ResolvedShortcut[] {
  return stored.flatMap((shortcut): ResolvedShortcut[] => {
    if (isCustomShortcut(shortcut)) {
      return [
        {
          id: shortcut.id,
          label: shortcut.custom.label,
          icon: iconForPath(shortcut.custom.path),
          destination: route(shortcut.custom.path),
        },
      ];
    }
    if (isRecordShortcut(shortcut)) {
      const { record } = shortcut;
      return [
        {
          id: shortcut.id,
          // « Modifier » + titre de la fiche (pas d'interpolation dans vt).
          label: `${vt("bo.home.pin.editPrefix", "Modifier")} ${
            record.label || vt("bo.home.pin.untitled", "Sans titre")
          }`,
          icon: RECORD_SHORTCUT_ICONS[record.type],
          destination: route(recordShortcutPath(record)),
        },
      ];
    }
    const entry = findCatalogShortcut(shortcut.id);
    const destination = entry && resolveDestination(entry.destination, ctx);
    if (!entry || !destination) return [];
    return [
      {
        id: entry.id,
        label: vt(entry.labelKey, entry.label),
        icon: entry.icon,
        destination,
      },
    ];
  });
}
